import extendNode from '../extendNode.mjs'
import useCommonNode from '../_COMMON/useCommonNode.jsx'

import {
    insertModuleTypes,
    menuItemsInsertExternal,
    menuItemsInsertModule,
} from '../menuItemsInsert.mjs'

import useMenuItemsInsertStatement from '../../menuItems/useMenuItemsInsertStatement.mjs'
import {NodeStylePane as StylePane} from '../../SidePanel/RightPanel/NodePane.jsx'
import meta from './metaAr.mjs'
import ArType from './ArType.jsx'
import PropertiesPane from './PropertiesPaneAr.jsx'
import PropertiesPaneBatch from './PropertiesPaneBatchAr.jsx'

const ArTypes = new Map(
    ArType
        .flat()
        .filter(a => Array.isArray(a))
        .map(([type, name, icon]) => [type, {icon, name, type}])
)

export default () => {
    const CommonNode = useCommonNode()
    const menuItemsInsertStatement = useMenuItemsInsertStatement()

    return extendNode(CommonNode, {
        ...meta,
        PropertiesPane,
        PropertiesPaneBatch,
        StylePane,

        canCopyStyle(map, node) {
            return true
        },

        canDeleteNode(map, node) {
            return this.canWriteTree(map, node)
        },

        canInsertStatement(map, node, statement) {
            if (! node.parent) {
                return false
            }

            const _p = map.nodeProxy(node.parent)

            if (! _p.canInsertStatement?.(statement)) {
                return false
            }

            const {arTypeCode} = node.data

            return (
                /^(CASE|CAT|LOOP)$/.test(arTypeCode) ||
                '1' === statement.statementContentType
            )
        },

        canLinkType(map, node, type) {
            return insertModuleTypes.includes(type)
        },

        canMountType(map, node, type) {
            return /^(AR|DOC|PIC|OLD|URL)$/.test(type)
        },

        canWriteStyle(map, node) {
            return this.canWriteData(map, node)
        },

        castFrom(map, node) {
            const {bizNodeType} = node.data
            CommonNode.castFrom.call(this, map, node)

            // 尽量匹配类型
            if (ArTypes.has(bizNodeType)) {
                node.data = {...node.data, arTypeCode: bizNodeType}
            }
        },

        getDesc(map, node) {
            const {arTypeCode} = node.data

            if (! arTypeCode || 'GENERAL' === arTypeCode) {
                return this.typeName(map, node.parent)
            }

            const {name = ''} = ArTypes.get(arTypeCode) ?? {}
            return name
        },

        getIcons(map, node) {
            const {arTypeCode} = node.data
            const {icon} = ArTypes.get(arTypeCode) ?? {}

            if (icon) {
                return [icon]
            }
            else {
                return []
            }
        },

        getRev(map, node) {
            return null
        },

        getStyle(map, node) {
            return {
                ...CommonNode.getStyle.call(this, map, node),
                backgroundColor: '#f8f7f7',
                shape: 'Rectangle',
            }
        },

        getTitle(map, node) {
            const {arTypeCode} = node.data
            const {name} = ArTypes.get(arTypeCode) ?? {}
            return name ?? CommonNode.getTitle.call(this, map, node)
        },

        menuItemsInsertCommon(map, node) {
            const items = [
                {type: 'AR'},
                {type: 'PIC'},
                {type: 'DOC'},
                {type: 'URL'},
            ]

            const filterStatements = menuItems => menuItems
                .map(item => {
                    if (item.children) {
                        const children = filterStatements(item.children)

                        if (0 < children.length) {
                            return {...item, children}
                        }
                        else {
                            return null
                        }
                    }
                    else {
                        const {statement} = item.data

                        if (this.canInsertStatement(map, node, statement)) {
                            return item
                        }
                        else {
                            return null
                        }
                    }
                })
                .filter(a => a)

            return [...items, ...filterStatements(menuItemsInsertStatement)]
        },

        menuItemsInsertProduct(map, node) {
            for (const n of node.chain) {
                const {bizNodeType} = n.data

                if ('BFDATAFLOW' === bizNodeType) {
                    return [
                        {type: 'BDSUMMARY'},
                        {type: 'BDSUMMARY_1'},
                        {type: 'BF'},
                    ]
                }
                else if ('AR' !== bizNodeType) {
                    break
                }
            }

            return [
                ...menuItemsInsertModule(map),
                ...menuItemsInsertExternal(map),
            ]
        },

        async onDoubleClick(map, node, event) {
            const {mapTypeCode} = map.data

            if (
                'CHG_REFERENCE_MAP' === mapTypeCode &&
                node.parent === map.root
            ) {
                const {arName} = node.data
                const leftRev = arName.replace(/^\d+\.(\d+)$/, '$1')
                const _p = map.nodeProxy(node.parent)
                const rightRev = _p.getRev()
                await _p.gotoDiff(leftRev, rightRev, event.target)
            }
            else {
                await CommonNode.onDoubleClick.call(this, map, node, event)
            }
        },

        async onInsertCommon(map, nodes, data) {
            if (data) {
                if ('statement' === data.type) {
                    const {root} = JSON.parse(data.statement.statementContent)

                    map.execute(() => {
                        const nodesToSelect = new Set

                        for (const node of nodes) {
                            const n = map.importTree(root)
                            map.appendChild(node, n)
                            nodesToSelect.add(n)
                        }

                        map.selectNodes(nodesToSelect)
                    })
                }
            }
            else {
                await CommonNode.onInsertCommon.call(this, map, nodes)
            }
        },

        async _atPush(map, node, event) {
            event.stopPropagation()
        },

        _fixChild(map, node, child) {
            this.castFrom(map, child)
            return true
        },

        _getDefaultDataFields(map, node) {
            const {bizNodeType} = node.parent?.data ?? {}

            const arTypeCode = (() => {
                if (/^(ALG_RULE|BDLOGIC_TARGET)$/.test(bizNodeType)) {
                    return 'CAT'
                }
                else if ('BDSUMMARY' === bizNodeType) {
                    return 'USER'
                }
                else if ('OU_OR' === bizNodeType) {
                    return 'EXECUTOR'
                }
                else {
                    return 'EXEC'
                }
            })()

            // 不需要根据类型自动填充文本，故不调用父类的方法
            return {arTypeCode}
        },
    })
}
